import { getAudioData } from './get-audio-data';

export interface SilenceDetectionParams {
  audioUrl: string;
  /** Start time in the source audio (seconds) - for analyzing a clip segment */
  startInSeconds?: number;
  /** Duration in the source audio (seconds). If not set, uses from start to end. */
  durationInSeconds?: number;
  /** Minimum silence duration to detect (ms). */
  minSilenceDurationMs: number;
  /** Silence threshold in dBFS. Values at or below this are considered silence. */
  silenceThresholdDb: number;
}

export interface SilenceSegment {
  startInSeconds: number;
  endInSeconds: number;
}

/** Downsample array by repeatedly averaging pairs (2^N reduction). */
function resizeHalfN(x: Float32Array, n: number): Float32Array {
  let out = new Float32Array(x);
  for (let i = 0; i < n; i++) {
    const len = out.length;
    const isOdd = len % 2 !== 0;
    const pad = isOdd ? 1 : 0;
    const padded = new Float32Array(len + pad);
    padded.set(out);
    if (isOdd) padded[len] = out[len - 1]!;
    const halfLen = (len + pad) >> 1;
    const next = new Float32Array(halfLen);
    for (let j = 0; j < halfLen; j++) {
      next[j] = (padded[j * 2]! + padded[j * 2 + 1]!) / 2;
    }
    out = next;
  }
  return out;
}

/** Moving average with given span (symmetric). */
function shiftAvg(x: Float32Array, span: number): Float32Array {
  const n = x.length;
  if (n <= 2 * span) return x;
  const result = new Float32Array(n - 2 * span);
  for (let i = span; i < n - span; i++) {
    let sum = 0;
    for (let k = -span; k <= span; k++) {
      sum += x[i + k]!;
    }
    result[i - span] = sum / (2 * span + 1);
  }
  return result;
}

/** Convert dB to linear amplitude ratio. */
function dbToRatio(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * Detect silence segments in audio using amplitude threshold and minimum duration.
 * Returns segments in seconds relative to the analyzed window (0 = start of clip).
 */
export async function detectSilenceSegments(
  params: SilenceDetectionParams,
): Promise<SilenceSegment[]> {
  const {
    audioUrl,
    startInSeconds = 0,
    durationInSeconds: durationParam,
    minSilenceDurationMs,
    silenceThresholdDb,
  } = params;

  const meta = await getAudioData(audioUrl);
  const sr = meta.sampleRate;
  const fullDuration = meta.durationInSeconds;
  const durationInSeconds =
    durationParam != null
      ? Math.min(durationParam, fullDuration - startInSeconds)
      : fullDuration - startInSeconds;

  if (durationInSeconds <= 0) return [];

  const startSample = Math.floor(startInSeconds * sr);
  const endSample = Math.min(
    Math.ceil((startInSeconds + durationInSeconds) * sr),
    meta.channelWaveforms[0]!.length,
  );
  const length = endSample - startSample;
  if (length <= 0) return [];

  const channel = meta.channelWaveforms[0]!;
  const amp = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    amp[i] = Math.abs(channel[startSample + i]!);
  }

  const downsampleN = 10;
  const blockSize = 1 << downsampleN;
  const nn = Math.floor(length / blockSize) * blockSize;
  if (nn < blockSize) return [];

  const ampAbs = amp.subarray(0, nn);

  const tK = resizeHalfN(
    Float32Array.from({ length: nn }, (_, i) => (startSample + i) / sr),
    downsampleN,
  );
  const ampK = resizeHalfN(ampAbs, downsampleN);
  const smoothed = shiftAvg(ampK, 1);

  const ampLim = Math.max(dbToRatio(silenceThresholdDb), 1e-10);

  const bSilence = new Uint8Array(smoothed.length);
  for (let i = 0; i < smoothed.length; i++) {
    bSilence[i] = smoothed[i]! < ampLim ? 1 : 0;
  }

  const diff = new Int8Array(bSilence.length + 1);
  for (let i = 1; i < bSilence.length; i++) {
    diff[i] = (bSilence[i] ?? 0) - (bSilence[i - 1] ?? 0);
  }

  const silenceSpans: [number, number][] = [];
  let spanStart = bSilence[0] === 1 ? 0 : -1;
  for (let i = 1; i < diff.length; i++) {
    if (diff[i] === 1) spanStart = i;
    if (diff[i] === -1 && spanStart >= 0) {
      silenceSpans.push([spanStart, i - 1]);
      spanStart = -1;
    }
  }
  if (spanStart >= 0) {
    silenceSpans.push([spanStart, bSilence.length - 1]);
  }

  const minSilenceSamples = Math.max(
    1,
    Math.floor((minSilenceDurationMs / 1000) * (sr / blockSize)),
  );

  const silenceSegments: SilenceSegment[] = [];
  const spanDetails: Array<{
    span: [number, number];
    spanLen: number;
    meanAmp: number;
    meanAmpPass: boolean;
    minSilenceSamplesPass: boolean;
    passedFilter: boolean;
    startSec: number;
    endSec: number;
  }> = [];

  for (const [s0, s1] of silenceSpans) {
    const spanLen = s1 - s0 + 1;
    const meanAmp =
      spanLen > 0
        ? smoothed.slice(s0, s1 + 1).reduce((a, b) => a + b, 0) / spanLen
        : 1;
    const meanAmpPass = meanAmp < ampLim;
    const minSilenceSamplesPass = spanLen >= minSilenceSamples;
    const passedFilter = meanAmpPass && minSilenceSamplesPass;

    // 블록 인덱스(smoothed) → 클립 기준 초. smoothed 길이가 nn보다 짧아서 샘플 기준으로 하면 끝이 잘리므로, 비율로 매핑해 한 번에 전체 제거 가능하게 함.
    const L = smoothed.length;
    const startSec = Math.max(0, (s0 / L) * durationInSeconds);
    const endSec = Math.min(
      durationInSeconds,
      ((s1 + 1) / L) * durationInSeconds,
    );

    if (passedFilter && endSec - startSec >= 0.001) {
      silenceSegments.push({
        startInSeconds: startSec,
        endInSeconds: endSec,
      });
    }

    spanDetails.push({
      span: [s0, s1],
      spanLen,
      meanAmp,
      meanAmpPass,
      minSilenceSamplesPass,
      passedFilter,
      startSec,
      endSec,
    });
  }

  if (typeof console !== 'undefined' && console.log) {
    const silenceCount = bSilence.reduce((a, b) => a + b, 0);
    console.log('[무음 제거]', {
      params: {
        startInSeconds,
        durationInSeconds,
        minSilenceDurationMs,
        silenceThresholdDb,
      },
      criteria: {
        ampLim,
        totalBlocks: smoothed.length,
        silentBlocks: silenceCount,
        minSilenceSamples,
      },
      silenceSpans,
      spanDetails,
      silenceSegments,
    });
  }

  return silenceSegments;
}

/**
 * Compute non-silence segments from silence segments (relative to clip 0..duration).
 * Returns intervals that should be kept.
 */
export function getNonSilenceSegments(
  durationInSeconds: number,
  silenceSegments: SilenceSegment[],
): SilenceSegment[] {
  if (silenceSegments.length === 0) {
    return [{ startInSeconds: 0, endInSeconds: durationInSeconds }];
  }

  const sorted = [...silenceSegments].sort(
    (a, b) => a.startInSeconds - b.startInSeconds,
  );
  const nonSilence: SilenceSegment[] = [];
  let pos = 0;

  for (const seg of sorted) {
    const start = Math.max(0, seg.startInSeconds);
    const end = Math.min(durationInSeconds, seg.endInSeconds);
    if (start > pos + 0.001) {
      nonSilence.push({ startInSeconds: pos, endInSeconds: start });
    }
    pos = Math.max(pos, end);
  }
  if (pos < durationInSeconds - 0.001) {
    nonSilence.push({ startInSeconds: pos, endInSeconds: durationInSeconds });
  }

  return nonSilence;
}
